import React from 'react';
import * as XLSX from 'xlsx';
import { Settings, Database, FileSpreadsheet, Download, Info } from 'lucide-react';
import { toast } from 'sonner';

// ─── helpers de geração de modelo ───────────────────────────────────────────

const gerarModeloClientes = () => {
  const wb = XLSX.utils.book_new();

  const dados = [
    [
      'CNPJ', 'RAZAO_SOCIAL', 'NOME_FANTASIA',
      'TELEFONE', 'EMAIL', 'CIDADE', 'UF', 'CEP', 'ENDERECO',
    ],
    [
      '12.345.678/0001-90', 'EMPRESA EXEMPLO LTDA', 'Empresa Exemplo',
      '(11) 99999-0001', 'contato@exemplo.com.br',
      'São Paulo', 'SP', '01310-100', 'Av. Paulista, 1000, Bela Vista',
    ],
    [
      '98.765.432/0001-10', 'COMERCIAL MODELO S/A', 'Comercial Modelo',
      '(41) 98888-0002', 'financeiro@modelo.com.br',
      'Curitiba', 'PR', '80230-901', 'Rua XV de Novembro, 500, Centro',
    ],
  ];

  const ws = XLSX.utils.aoa_to_sheet(dados);

  ws['!cols'] = [
    { wch: 22 }, // CNPJ
    { wch: 36 }, // RAZAO_SOCIAL
    { wch: 26 }, // NOME_FANTASIA
    { wch: 18 }, // TELEFONE
    { wch: 30 }, // EMAIL
    { wch: 18 }, // CIDADE
    { wch:  6 }, // UF
    { wch: 12 }, // CEP
    { wch: 40 }, // ENDERECO
  ];

  XLSX.utils.book_append_sheet(wb, ws, 'Clientes');
  XLSX.writeFile(wb, 'modelo_clientes.xlsx');
  toast.success('Modelo de clientes baixado!');
};

const gerarModeloMateriais = () => {
  const wb = XLSX.utils.book_new();

  const dados = [
    [
      'FE', 'NOME', 'PESO_KG', 'MILHEIRO', 'COMISSAO_PCT',
      'SEGMENTO', 'IPI_PCT', 'REPRESENTADA',
      'COMPRIMENTO', 'LARGURA', 'ALTURA', 'GRAMATURA',
    ],
    [
      'FE-00001', 'CAIXA AMERICANA 30X20X15 DUPLEX', 0.25, 850.00, 3.5,
      'CAIXA', 15, 'Conpel',
      300, 200, 150, '',
    ],
    [
      'FE-00002', 'CHAPA ONDULADA 2100X1050MM TRIPLEX', 1.80, 1250.00, 4.0,
      'CHAPA', 15, 'Conpel',
      2100, 1050, '', 150,
    ],
  ];

  const ws = XLSX.utils.aoa_to_sheet(dados);

  ws['!cols'] = [
    { wch: 12 }, // FE
    { wch: 40 }, // NOME
    { wch: 10 }, // PESO_KG
    { wch: 10 }, // MILHEIRO
    { wch: 14 }, // COMISSAO_PCT
    { wch: 14 }, // SEGMENTO
    { wch: 10 }, // IPI_PCT
    { wch: 16 }, // REPRESENTADA
    { wch: 13 }, // COMPRIMENTO
    { wch: 10 }, // LARGURA
    { wch: 10 }, // ALTURA
    { wch: 12 }, // GRAMATURA
  ];

  XLSX.utils.book_append_sheet(wb, ws, 'Materiais');
  XLSX.writeFile(wb, 'modelo_materiais.xlsx');
  toast.success('Modelo de materiais baixado!');
};

// ─── componente de seção reutilizável ────────────────────────────────────────

const SecaoConfig = ({ icone: Icone, titulo, children }) => (
  <div className="mb-6">
    <div className="flex items-center gap-2 mb-3">
      <Icone size={14} className="text-[#0A3D73]" />
      <h2 className="text-xs font-black uppercase tracking-widest text-[#0A3D73]">{titulo}</h2>
      <div className="flex-1 border-t border-slate-200" />
    </div>
    {children}
  </div>
);

// ─── página ──────────────────────────────────────────────────────────────────

const COLUNAS_CLIENTES = ['CNPJ', 'RAZAO_SOCIAL', 'NOME_FANTASIA', 'TELEFONE', 'EMAIL', 'CIDADE', 'UF', 'CEP', 'ENDERECO'];
const COLUNAS_MATERIAIS = ['FE', 'NOME', 'PESO_KG', 'MILHEIRO', 'COMISSAO_PCT', 'SEGMENTO', 'IPI_PCT', 'REPRESENTADA', 'COMPRIMENTO', 'LARGURA', 'ALTURA', 'GRAMATURA'];

const ModeloCard = ({ titulo, descricao, colunas, cor, onDownload }) => {
  const bg = cor === 'blue'
    ? { icon: 'bg-blue-100', iconColor: 'text-[#0A3D73]', btn: 'bg-[#0A3D73] hover:bg-[#082D54]' }
    : { icon: 'bg-emerald-100', iconColor: 'text-emerald-700', btn: 'bg-emerald-700 hover:bg-emerald-800' };

  const preview = colunas.slice(0, 3);
  const resto   = colunas.length - 3;

  return (
    <div className="border border-slate-200 rounded-xl p-4 hover:border-[#0A3D73]/30 hover:bg-blue-50/30 transition-all">
      <div className="flex items-start gap-3 mb-4">
        <div className={`w-9 h-9 rounded-lg ${bg.icon} flex items-center justify-center flex-shrink-0`}>
          <FileSpreadsheet size={16} className={bg.iconColor} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-black text-slate-800">{titulo}</p>
          <p className="text-xs text-slate-500 font-semibold mt-0.5 leading-relaxed">{descricao}</p>
          <div className="flex flex-wrap gap-1 mt-2">
            {preview.map(tag => (
              <span key={tag} className="px-1.5 py-0.5 bg-slate-100 text-slate-500 text-[9px] font-black uppercase rounded border border-slate-200">
                {tag}
              </span>
            ))}
            {resto > 0 && (
              <span className="px-1.5 py-0.5 bg-slate-100 text-slate-400 text-[9px] font-bold rounded border border-slate-200">
                + {resto} mais
              </span>
            )}
          </div>
        </div>
      </div>
      <button
        onClick={onDownload}
        className={`w-full flex items-center justify-center gap-2 ${bg.btn} text-white text-xs font-black uppercase px-4 h-9 rounded-lg transition-colors shadow-sm`}
      >
        <Download size={13} /> Baixar Modelo
      </button>
    </div>
  );
};

const Configuracoes = () => (
  <div className="p-4 md:p-6 bg-[#E9EEF2] min-h-screen">

    {/* ── HEADER ── */}
    <div className="mb-6">
      <h1 className="text-2xl font-black text-slate-900 flex items-center gap-2">
        <Settings size={20} className="text-[#0A3D73]" strokeWidth={2} />
        Configurações
      </h1>
      <p className="text-xs text-slate-500 font-semibold mt-0.5">Opções do sistema</p>
    </div>

    {/* ── SEÇÃO: IMPORTAÇÃO DE DADOS ── */}
    <SecaoConfig icone={Database} titulo="Importação de Dados">
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">

        <div className="px-5 py-4 border-b border-slate-100">
          <h3 className="text-sm font-black text-slate-800">Modelos de Planilha</h3>
          <p className="text-xs text-slate-500 font-semibold mt-0.5">
            Baixe o modelo correto, preencha os dados e importe via o botão{' '}
            <span className="font-black text-slate-700">"Importar Planilha"</span>{' '}
            na página de Clientes ou Materiais.
          </p>
        </div>

        <div className="p-5">
          {/* Cards lado a lado */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <ModeloCard
              titulo="Modelo Clientes"
              descricao={`${COLUNAS_CLIENTES.length} colunas · CNPJ, Razão Social, Fantasia, Cidade…`}
              colunas={COLUNAS_CLIENTES}
              cor="blue"
              onDownload={gerarModeloClientes}
            />
            <ModeloCard
              titulo="Modelo Materiais"
              descricao={`${COLUNAS_MATERIAIS.length} colunas · FE, Nome, Peso, Milheiro, Comissão…`}
              colunas={COLUNAS_MATERIAIS}
              cor="green"
              onDownload={gerarModeloMateriais}
            />
          </div>

          {/* Instrução passo a passo */}
          <div className="flex items-start gap-3 bg-blue-50 border border-blue-100 rounded-xl p-4">
            <div className="w-5 h-5 rounded-full bg-[#0A3D73] flex items-center justify-center flex-shrink-0 mt-0.5">
              <Info size={10} className="text-white" />
            </div>
            <div>
              <p className="text-xs font-black text-[#0A3D73] mb-1.5">Como usar</p>
              <ol className="text-xs text-slate-600 font-semibold space-y-1 list-decimal list-inside">
                <li>Baixe o modelo acima e abra no Excel ou Google Planilhas</li>
                <li>Preencha as linhas com seus dados — <span className="font-black">não altere os cabeçalhos</span></li>
                <li>Acesse <span className="font-black">Clientes</span> ou <span className="font-black">Materiais</span> e clique em "Importar Planilha"</li>
                <li>Registros com CNPJ ou FE já existentes são ignorados automaticamente</li>
              </ol>
            </div>
          </div>
        </div>
      </div>
    </SecaoConfig>

    {/*
      ── PLACEHOLDER — futuras seções ─────────────────────────────────────────
      Próximas seções planejadas (adicionar cada uma como <SecaoConfig>):
        • Personalização — logo, nome da empresa, dados do rodapé de PDF
        • Onboarding      — wizard de configuração inicial do sistema
        • Notificações    — regras de alerta de vencimento / clientes inativos
        • Integrações     — webhooks, exportações automáticas, SFTP
      ─────────────────────────────────────────────────────────────────────── */}

  </div>
);

export default Configuracoes;
